import {
  ColumnDef,
  flexRenderComponent,
} from '@3ddv/software-division-components/backoffice/table';
import { gsActionsColumn } from '../../shared/ui/table/gs-table.columns';
import { GroupLink } from './models/group-links.model';
import { GroupLinkUrlCellComponent } from './cells/gs-group-link-url-cell.component';

export interface GroupLinkColumnsConfig {
  getUrlDisplay: (groupLink: GroupLink) => string;
  onCopyUrl: (groupLink: GroupLink) => void;
  onToggleActive: (groupLink: GroupLink) => void;
  onEdit: (groupLink: GroupLink) => void;
  onDelete: (groupLink: GroupLink) => void;
  canEdit?: boolean;
}

export function buildGroupLinkColumns(
  config: GroupLinkColumnsConfig
): ColumnDef<GroupLink>[] {
  const nameColumn: ColumnDef<GroupLink> = {
    id: 'name',
    header: 'Name',
    accessorFn: groupLink => groupLink.name,
  };

  const buyerTypeColumn: ColumnDef<GroupLink> = {
    id: 'buyerType',
    header: 'Buyer Type',
    accessorFn: groupLink => groupLink.defaultBuyerTypeId,
  };

  const urlColumn: ColumnDef<GroupLink> = {
    id: 'url',
    header: 'URL',
    cell: context => {
      const groupLink = context.row.original;

      return flexRenderComponent(GroupLinkUrlCellComponent, {
        inputs: {
          display: config.getUrlDisplay(groupLink),
        },
        outputs: {
          copied: () => config.onCopyUrl(groupLink),
        },
      });
    },
    enableSorting: false,
  };

  const actionsColumn = gsActionsColumn<GroupLink>({
    actions: [
      {
        id: 'toggle',
        label: groupLink => (groupLink.isActive ? 'Disable' : 'Enable'),
        tone: groupLink => (groupLink.isActive ? 'negative' : 'positive'),
        dataCy: groupLink => `event-enable-button-${groupLink.id}`,
      },
      {
        id: 'edit',
        label: 'Edit',
        dataCy: groupLink => `group-link-edit-button-${groupLink.id}`,
      },
      {
        id: 'delete',
        label: 'Delete',
        destructive: true,
        dataCy: groupLink => `group-link-delete-button-${groupLink.id}`,
      },
    ],
    onSelect: (actionId, groupLink) => {
      if (actionId === 'toggle') {
        config.onToggleActive(groupLink);
      } else if (actionId === 'edit') {
        config.onEdit(groupLink);
      } else if (actionId === 'delete') {
        config.onDelete(groupLink);
      }
    },
    rowDataCy: groupLink => `group-link-row-${groupLink.id}`,
  });

  return [
    nameColumn,
    buyerTypeColumn,
    urlColumn,
    ...((config.canEdit ?? true) ? [actionsColumn] : []),
  ];
}
